import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const IRISH_IMG =
  "https://images.unsplash.com/photo-1627660167353-b1140904cdaf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function IrishIdentity() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "14%"]);

  return (
    <section id="ireland" ref={ref} data-testid="ireland-section" className="bg-base-bg py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Big spotlight image, clipped */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden aspect-[16/9] md:aspect-[21/9] border border-emerald-900/20"
        >
          <motion.img
            src={IRISH_IMG}
            alt="Irish coastline and rolling green fields"
            style={{ y: imgY, scale: 1.18 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/20" aria-hidden="true" />
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          <div className="lg:col-span-3">
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
              <span className="font-serif text-7xl md:text-8xl text-emerald-900/20 leading-none">02</span>
              <p className="mt-4 text-[0.72rem] uppercase tracking-[0.22em] text-emerald-900/50">The heart</p>
            </motion.div>
          </div>

          <div className="lg:col-span-9 lg:pl-4">
            <motion.h2
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-emerald-900 max-w-3xl"
            >
              Proudly Irish. <span className="italic text-emerald-900/60">Globally engaged.</span>
            </motion.h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 text-base md:text-lg leading-relaxed text-base-text/80">
              <motion.p custom={1} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
                While our investments span the globe, our foundation is firmly planted in Ireland.
                We are deeply committed to supporting Irish activities, fostering local enterprise,
                and contributing to the society that shaped us.
              </motion.p>
              <motion.p custom={2} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
                For us, a successful investment is one that allows us to give back—through
                philanthropic contributions and charitable initiatives that strengthen Irish
                communities.
              </motion.p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
